<script lang="ts">
  /**
   * DifficultySelectScreen - Choose difficulty for Monster Mode
   */

  import { fade, scale } from 'svelte/transition';
  import StarField from '../ui/StarField.svelte';

  // Props
  interface Props {
    onSelectDifficulty?: (difficulty: 'easy' | 'medium' | 'advanced') => void;
    onBack?: () => void;
  }

  let {
    onSelectDifficulty = () => {},
    onBack = () => {}
  }: Props = $props();
</script>

<div class="difficulty-screen">
  <StarField />

  <div class="content">
    <!-- Back Button -->
    <button class="back-button" onclick={onBack} aria-label="Go back">
      <span class="back-icon">←</span> Back
    </button>

    <!-- Title -->
    <h1 class="title">CHOOSE DIFFICULTY</h1>

    <!-- Difficulty Buttons -->
    <div class="button-container">
      <button
        class="difficulty-button easy"
        onclick={() => onSelectDifficulty('easy')}
        style="animation-delay: 0s"
      >
        <span class="icon">🌱</span>
        <div class="text-content">
          <span class="button-title">EASY</span>
          <span class="button-subtitle">Word shown · No penalties</span>
        </div>
      </button>

      <button
        class="difficulty-button medium"
        onclick={() => onSelectDifficulty('medium')}
        style="animation-delay: 0.1s"
      >
        <span class="icon">🌿</span>
        <div class="text-content">
          <span class="button-title">MEDIUM</span>
          <span class="button-subtitle">Hidden word · Sequence bonus</span>
        </div>
      </button>

      <button
        class="difficulty-button advanced"
        onclick={() => onSelectDifficulty('advanced')}
        style="animation-delay: 0.2s"
      >
        <span class="icon">🌳</span>
        <div class="text-content">
          <span class="button-title">ADVANCED</span>
          <span class="button-subtitle">Hidden word · Point penalties</span>
        </div>
      </button>
    </div>
  </div>
</div>

<style>
  .difficulty-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xl);
    padding: var(--space-lg);
    width: 100%;
    max-width: 600px;
  }

  /* Back Button */
  .back-button {
    position: absolute;
    top: var(--space-lg);
    left: var(--space-lg);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-lg);
    border: none;
    border-radius: var(--radius-md);
    background: var(--color-space-blue);
    color: white;
    font-size: var(--font-lg);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .back-button:hover {
    background: var(--color-cosmic-purple);
    transform: translateX(-4px);
  }

  .back-icon {
    font-size: 24px;
  }

  /* Title */
  .title {
    font-size: var(--font-display);
    font-weight: bold;
    color: var(--color-bright-yellow);
    margin: 0;
    margin-top: 60px;
    text-align: center;
    text-shadow: 0 0 20px rgba(252, 211, 77, 0.5);
  }

  /* Button Container */
  .button-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
    width: 100%;
  }

  /* Difficulty Buttons */
  .difficulty-button {
    display: flex;
    align-items: center;
    gap: var(--space-lg);
    padding: var(--space-lg);
    width: 100%;
    border: 3px solid transparent;
    border-radius: var(--radius-md);
    font-size: var(--font-lg);
    cursor: pointer;
    transition: all 0.2s ease;
    animation: slideIn 0.5s ease forwards;
    opacity: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .difficulty-button .icon {
    font-size: 48px;
    flex-shrink: 0;
  }

  .text-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    flex: 1;
  }

  .button-title {
    font-size: 24px;
    font-weight: bold;
    color: white;
  }

  .button-subtitle {
    font-size: 14px;
    opacity: 0.8;
    color: white;
  }

  /* Easy Difficulty - Soft Green */
  .difficulty-button.easy {
    background: linear-gradient(135deg, #10B981 0%, #86EFAC 100%);
  }

  .difficulty-button.easy:hover {
    border-color: #86EFAC;
    transform: scale(1.03);
    box-shadow: 0 0 30px rgba(134, 239, 172, 0.5);
  }

  /* Medium Difficulty - Orange */
  .difficulty-button.medium {
    background: linear-gradient(135deg, #F97316 0%, #FB923C 100%);
  }

  .difficulty-button.medium:hover {
    border-color: #FB923C;
    transform: scale(1.03);
    box-shadow: 0 0 30px rgba(251, 146, 60, 0.5);
  }

  /* Advanced Difficulty - Purple */
  .difficulty-button.advanced {
    background: linear-gradient(135deg, #A855F7 0%, #C084FC 100%);
  }

  .difficulty-button.advanced:hover {
    border-color: #C084FC;
    transform: scale(1.03);
    box-shadow: 0 0 30px rgba(192, 132, 252, 0.5);
  }

  .difficulty-button:active {
    transform: scale(0.98);
  }

  /* Responsive Design */
  @media (max-width: 767px) {
    .title {
      font-size: 28px;
      margin-top: 80px;
    }

    .back-button {
      top: var(--space-md);
      left: var(--space-md);
      padding: var(--space-sm) var(--space-md);
      font-size: var(--font-body);
    }

    .difficulty-button {
      gap: var(--space-md);
    }

    .difficulty-button .icon {
      font-size: 36px;
    }

    .button-title {
      font-size: 20px;
    }

    .button-subtitle {
      font-size: 12px;
    }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .difficulty-button {
      max-width: 500px;
      margin: 0 auto;
    }
  }

  @media (min-width: 1024px) {
    .difficulty-button {
      max-width: 450px;
    }

    .title {
      font-size: 48px;
    }
  }
</style>
